import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    FileAttachmentApiService,
    ReferenceBookResponse,
    RefundApiService,
    RefundResponse,
    RefundsResponse,
    RefundStatus,
    RefundType,
} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, downloadFromBlobResponse, LocalizationService, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {combineLatest, of, Subscription, tap} from 'rxjs';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterField,
    getFormattedDateOrNull,
} from '../../components/full-table/filter/filter.component';
import {mergeTableEvent, TableEvent} from '../../components/full-table/full-table.component';
import {ButtonColumn, Column, ColumnType, Row, StatusColumn} from '../../components/full-table/table/table.component';
import {mapRefundStatusToStyle, StatusStyle} from '../../components/status/status.component';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {CACHE_EXPIRATION_INTERVAL} from '../../domain/constants';
import {DictionaryHolderService, findValueInDictionary} from '../../domain/dictionary-holder.service';
import {INCIDENT_ID, REFUND_ID} from '../../domain/query-params.keys';
import {RefundsFilterHolderService} from '../../domain/refunds-filter-holder.service';
import {Path} from '../../path';

@Component({
    templateUrl: 'refunds.component.html',
    standalone: false,
})
export class RefundsComponent implements OnDestroy {
    tabs: RefundsTab[] = ['tabAllRefunds', 'tabMyRefunds', 'tabRelatedRefunds', 'tabOrganizationsRefunds'];
    activeTab: RefundsTab;
    selectedTabIndex: number;
    commonFilters: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'refundType',
            dictionaryName: 'refund_type',
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'refundStatus',
            dictionaryName: 'refund_status',
            values: [],
        },
        <FilterField>{
            type: 'text',
            name: 'refundAuthor',
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'senderBank',
            dictionaryName: 'bin',
            filterBy: ['BANK', 'PAYM', 'MFO', 'OPERATOR'],
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'receiverBank',
            dictionaryName: 'bin',
            filterBy: ['BANK', 'PAYM', 'MFO', 'OPERATOR'],
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'refundRelatedOrganizations',
            dictionaryName: 'bin',
            filterBy: ['BANK', 'PAYM', 'MFO', 'OPERATOR'],
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'refundGovernment',
            dictionaryName: 'bin',
            filterBy: ['IO', 'GOV'],
            values: [],
        },
        <FilterField>{
            type: 'dateRange',
            name: 'separatedDateRange',
        },
        <FilterField>{
            type: 'amountRange',
            name: 'amountRange',
            translationPrefix: 'refunds',
        },
    ];
    filterFields: FilterField[] = [];
    incidentId: string;
    dataSource: Row[] = [];

    private refundsSubscription: Subscription;

    constructor(
        private activatedRoute: ActivatedRoute,
        private refundsApiService: RefundApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private routerService: RouterService,
        private localizationService: LocalizationService,
        private authHolderService: AuthHolderService,
        private fileAttachmentApiService: FileAttachmentApiService,
        public filterHolderService: RefundsFilterHolderService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.queryParamMap.get(INCIDENT_ID);
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as RefundsTab || 'tabAllRefunds';
        this.selectedTabIndex = this.tabs.findIndex(tab => tab == this.activeTab);
        this.handleNavigation(this.selectedTabIndex);
    }

    ngOnDestroy(): void {
        this.refundsSubscription?.unsubscribe();
    }

    handleNavigation($event: number): void {
        this.selectedTabIndex = $event >= 0 ? $event : 0;
        this.activeTab = this.tabs[this.selectedTabIndex];
        this.routerService.navigate(
            Path.REFUNDS,
            {
                queryParams: {[TAB]: this.activeTab},
                queryParamsHandling: 'merge',
            });
        this.filterFields = [...this.commonFilters];
        this.loadRefunds(this.filterHolderService.getState(this.activeTab).tableEvent);
    }

    loadRefunds(tableEvent: TableEvent): void {
        this.refundsSubscription?.unsubscribe();

        const mergedEvent = mergeTableEvent(this.filterHolderService.getState(this.activeTab).tableEvent, tableEvent);
        const activeTabState = this.filterHolderService.storeTableState(this.activeTab, mergedEvent);
        const filter = activeTabState.tableEvent.filter;
        this.dataSource = [];
        const isCacheExpired = dayjs().diff(activeTabState.cachedTime, 'seconds') >= CACHE_EXPIRATION_INTERVAL;
        const refunds$ = !activeTabState.response || activeTabState.tableEvent !== tableEvent || isCacheExpired
            ? this.refundsApiService.getRefunds({
                incidentId: this.incidentId,
                query: filter.query,
                limit: activeTabState.tableEvent.page.pageSize,
                offset: activeTabState.tableEvent.page.pageOffset,
                sortBy: activeTabState.tableEvent.sortBy?.columnName,
                sortOrder: activeTabState.tableEvent.sortBy?.direction,
                statuses: fieldExtractor(filter.refundStatus) as RefundStatus[],
                types: fieldExtractor(filter.refundType) as RefundType[],
                createdDateFrom: getFormattedDateOrNull(
                    filter?.separatedDateRange?.startDate?.date,
                    DATE_TIME_SERVER_FORMAT,
                ),
                createdDateTo: getFormattedDateOrNull(
                    filter?.separatedDateRange?.endDate?.date,
                    DATE_TIME_SERVER_FORMAT,
                ),
                amountFrom: filter?.amountRange?.from,
                amountTo: filter?.amountRange?.to,
                senderBins: fieldExtractor(filter.senderBank) as string[],
                receiverBins: fieldExtractor(filter.receiverBank) as string[],
                author: filter.refundAuthor,
                createdByUsername: filter.refundCreatedByUsername,
                relatedBins: fieldExtractor(filter.refundRelatedOrganizations) as string[],
                prosecutorOfficeBins: fieldExtractor(filter.refundGovernment) as string[],
                initiatorBins: filter.refundOrganizations,
                excludeInitiatorBins: filter.refundExcludeAuthorOrganizations,
            }).pipe(tap(response => this.filterHolderService.storeResponse(this.activeTab, response)))
            : of(activeTabState.response);
        this.refundsSubscription = combineLatest([
            this.dictionaryHolderService.getReference('bin'),
            this.dictionaryHolderService.getReference('refund_type'),
            this.dictionaryHolderService.getReference('refund_status'),
            this.dictionaryHolderService.getReference('currency'),
            refunds$,
        ])
            .pipe(rxSpinner(tableEvent.spinnerId))
            .subscribe(([organizations, refundTypes, refundStatuses, currencies, response]) => {
                this.buildDataSource(response, refundTypes, refundStatuses, organizations, currencies);
            });
    }

    navigateToIncidents(): void {
        this.routerService.navigate(Path.INCIDENTS);
    }

    private buildDataSource(
        response: RefundsResponse,
        refundTypes: ReferenceBookResponse[],
        refundStatuses: ReferenceBookResponse[],
        organizations: ReferenceBookResponse[],
        currencies: ReferenceBookResponse[],
    ): void {
        this.dataSource = response.refunds.map(item => {
            let columnButton: Column;
            let buttonLabel = '';
            const paymentOrder = item.attachments.find(attachment => attachment.docType === 'PAYMENT_ORDER')?.files?.[0];
            if (paymentOrder && item.status === RefundStatus.EXECUTED) {
                buttonLabel = 'i18n_af_buttonDownloadPaymentOrder';
            } else if (item.processingBin === this.authHolderService.organizationBin && !FINAL_REFUND_STATUSES.includes(item.status)) {
                buttonLabel = 'i18n_af_button_processRefund';
            }
            if (buttonLabel) {
                columnButton = <ButtonColumn>{
                    type: ColumnType.BUTTON,
                    buttonType: 'secondary',
                    icon: 'ic_open',
                    buttonSize: 'extra-small',
                    spinnerId: paymentOrder ? `downloadFile_${paymentOrder.id}` : undefined,
                    text: buttonLabel,
                    click: () => {
                        if (item.status === RefundStatus.EXECUTED) {
                            this.downloadPaymentOrder(paymentOrder.id);
                        } else {
                            this.processRefund(item.id);
                        }
                    },
                };
            } else {
                columnButton = <Column>{
                    type: ColumnType.HIDDEN,
                };
            }
            const refundStatusInfo = getRefundStatusInfo(
                refundStatuses,
                item,
                this.localizationService,
            );
            return <Row>{
                rowStyle: item.status === RefundStatus.REJECTED ? 'error-light' : '',
                click: () => this.routerService.navigate(Path.REFUND_DETAILS,
                    {pathParams: {[REFUND_ID]: item.id}, queryParams: {[TAB]: this.activeTab}}),
                created_date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.created_date,
                },
                refundType: <Column>{
                    type: ColumnType.TEXT,
                    text: refundTypes.find(status => status.code === item.type)?.name_ru ?? '-',
                },
                amount: <Column>{
                    type: ColumnType.MONEY,
                    text: item.transactionAmount?.toString() ?? '-',
                },
                currency: <Column>{
                    type: ColumnType.TEXT,
                    text: findValueInDictionary(currencies, item.transactionCurrency, 'code1') ?? '-',
                },
                status: <StatusColumn>{
                    type: ColumnType.STATUS,
                    text: refundStatusInfo.text,
                    style: refundStatusInfo.style,
                },
                executedDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    text: item.refundDateTime,
                },
                createdBy: <Column>{
                    type: ColumnType.TEXT,
                    text: item.created_by ?? '-',
                },
                bankSender: <Column>{
                    type: ColumnType.TEXT,
                    text: organizations.find(dictionary => dictionary.code === item.senderBin)?.name_ru ?? '-',
                },
                bankReceiver: <Column>{
                    type: ColumnType.TEXT,
                    text: organizations.find(dictionary => dictionary.code === item.receiverBin)?.name_ru ?? '-',
                },
                processingBin: <Column>{
                    type: ColumnType.TEXT,
                    text: organizations.find(dictionary => dictionary.code === item.processingBin)?.name_ru ?? '-',
                },
                columnButton: columnButton,
                id: <Column>{
                    type: ColumnType.TEXT,
                    text: item.id,
                    cellStyle: {
                        ellipsisTextLarge: true,
                    },
                },
            };
        });
    }

    private processRefund(id: string): void {
        this.routerService.navigate(Path.REFUND_DETAILS, {pathParams: {[REFUND_ID]: id}, queryParams: {[TAB]: this.activeTab}});
    }

    private downloadPaymentOrder(fileId: string): void {
        this.fileAttachmentApiService.downloadFile(fileId, 'response')
            .pipe(rxSpinner(`downloadFile_${fileId}`))
            .subscribe(response => downloadFromBlobResponse(response));
    }
}

export interface RefundTabInfo {
    tableEvent: TableEvent;
    response?: RefundsResponse;
    cachedTime: dayjs.Dayjs;
}

export declare type RefundsTab =
    'tabAllRefunds'
    | 'tabMyRefunds'
    | 'tabRelatedRefunds'
    | 'tabOrganizationsRefunds';

export const FINAL_REFUND_STATUSES: RefundStatus[] = [RefundStatus.DELETED, RefundStatus.EXECUTED, RefundStatus.REJECTED];

export function getRefundStatusInfo(
    refundStatuses: ReferenceBookResponse[],
    refund: RefundResponse,
    localizationService: LocalizationService,
): RefundStatusInfo {
    let refundStatus: RefundStatuses = refund.status;
    let refundStatusText = refundStatuses.find(status => status.code === refundStatus)?.name_ru ?? '-';
    if (refundStatus === RefundStatus.REJECTED) {
        refundStatus = (refund.rejectedBin === refund.prosecutorOfficeBin && RefundStatusExtended.REJECTED_BY_PROSECUTOR)
            || (refund.rejectedBin === refund.senderBin && RefundStatusExtended.REJECTED_BY_SENDER)
            || (refund.rejectedBin === refund.receiverBin && RefundStatusExtended.REJECTED_BY_RECEIVER);

        if (!refundStatus) {
            refundStatus = RefundStatus.REJECTED;
        }
        refundStatusText = localizationService.getLocalizedKey(`i18n_af_labelRefundStatus#${refundStatus}`);
    }

    return <RefundStatusInfo>{
        text: refundStatusText,
        style: mapRefundStatusToStyle(refundStatus),
    };
}

export interface RefundStatusInfo {
    style: StatusStyle;
    text: string;
}

export declare type RefundStatuses = RefundStatus | RefundStatusExtended;

export enum RefundStatusExtended {
    REJECTED_BY_PROSECUTOR = 'REJECTED_BY_PROSECUTOR',
    REJECTED_BY_SENDER = 'REJECTED_BY_SENDER',
    REJECTED_BY_RECEIVER = 'REJECTED_BY_RECEIVER',
}
